/**
 * Tiny fetch wrapper used everywhere in the app.
 *
 *  * ``credentials: "include"`` → cookies (incl. ``finder_session``)
 *    are sent with every request. Required for auth-gated endpoints.
 *  * 401 responses bubble up as ``ApiError(401)`` and are intercepted
 *    by the auth store's ``hydrate()`` (which falls back to anon
 *    state) and by route guards (which redirect to ``/login``).
 *  * Errors don't dump arbitrary HTML into the message — we strip
 *    to a sensible string so toasts/UI don't render junk.
 */

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const j = await res.json();
        detail = (j && (j.detail || j.message)) || JSON.stringify(j);
      } else {
        detail = await res.text();
      }
    } catch {
      detail = "";
    }
    throw new ApiError(detail || res.statusText, res.status);
  }
  // 204 / empty body
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
